import { useEffect, useState } from 'react';
import { Loader2, Pencil, Package, Save, Zap, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);

/**
 * Admin tab: one row per vendor with live product count / avg margin
 * plus admin-editable metadata (default_discount_pct, notes, contact).
 * A "Filter catalog" shortcut jumps to the Products tab pre-filtered.
 */
export default function AdminVendorsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/suppliers');
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load vendors');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyDefault = async (v, overwrite) => {
    if (v.default_discount_pct == null) {
      toast.info('Set a default discount first');
      return;
    }
    const msg = overwrite
      ? `Overwrite every ${v.name} product's discount % with ${v.default_discount_pct}%? Costs will be recomputed from wholesale.`
      : `Apply ${v.default_discount_pct}% to ${v.name} items that don't have their own discount yet?`;
    if (!window.confirm(msg)) return;
    setBusy(v.name);
    try {
      const { data } = await api.post(`/admin/vendors/${encodeURIComponent(v.name)}/apply-default-discount`, null, {
        params: { overwrite },
      });
      toast.success(`Updated ${data.touched} product${data.touched === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk apply failed');
    } finally { setBusy(''); }
  };

  if (loading) {
    return <div className="container-x py-10 sans text-neutral-500">Loading vendors…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-vendors-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="eyebrow">Vendors</p>
          <h2 className="h-display text-2xl md:text-3xl mt-1">Supplier directory</h2>
          <p className="sans text-sm text-neutral-500 mt-2 max-w-2xl">
            Every supplier feeding the catalog with live product counts, average margin,
            and a default discount you can roll onto every one of their items in one tap.
          </p>
        </div>
        <p className="sans text-xs tracking-widest uppercase text-neutral-500">
          {rows.length} vendor{rows.length === 1 ? '' : 's'}
        </p>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {rows.map((v) => (
          <div key={v.name} className="border border-neutral-200 p-4" data-testid={`vendor-card-${v.name}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="serif text-lg leading-tight">{v.name}</p>
                <p className="sans text-[11px] text-neutral-500 mt-0.5">
                  {v.product_count} product{v.product_count === 1 ? '' : 's'}
                  {v.brands?.length ? ` · ${v.brands.slice(0, 3).join(', ')}${v.brands.length > 3 ? '…' : ''}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditing(v)}
                className="p-2 text-neutral-500 active:text-black"
                data-testid={`vendor-edit-${v.name}`}
                title="Edit vendor"
              >
                <Pencil size={18} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-neutral-100">
              <div>
                <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Avg cost</p>
                <p className="serif text-sm mt-0.5">{money(v.avg_cost)}</p>
              </div>
              <div>
                <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Avg wholesale</p>
                <p className="serif text-sm mt-0.5">{money(v.avg_wholesale)}</p>
              </div>
              <div>
                <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Avg margin</p>
                <p className="serif text-sm mt-0.5">{pct(v.avg_margin_pct)}</p>
              </div>
              <div className="col-span-3">
                <p className="sans text-[9px] tracking-widest uppercase text-neutral-500">Default % off wholesale</p>
                <p className="serif text-sm mt-0.5">{v.default_discount_pct == null ? '—' : `${v.default_discount_pct}%`}</p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => window.location.assign(`/admin?tab=products&supplier=${encodeURIComponent(v.name)}`)}
                className="btn-outline h-10 text-xs"
                data-testid={`vendor-filter-catalog-${v.name}`}
              >
                <Package size={14} /> Filter catalog
              </button>
              <button
                type="button"
                onClick={() => applyDefault(v, false)}
                disabled={busy === v.name || v.default_discount_pct == null || v.product_count === 0}
                className="btn-outline h-10 text-xs disabled:opacity-40"
                data-testid={`vendor-apply-default-${v.name}`}
              >
                {busy === v.name ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Apply default
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <table className="hidden md:table w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Vendor</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Products</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Avg Cost</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Avg Wholesale</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Avg Margin</th>
            <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Default % off</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v) => (
            <tr key={v.name} className="border-b border-neutral-100">
              <td className="py-3">
                <p className="serif">{v.name}</p>
                <p className="sans text-[11px] text-neutral-500 mt-0.5 truncate max-w-[280px]">
                  {v.brands?.length ? v.brands.slice(0, 4).join(', ') : '—'}
                </p>
              </td>
              <td className="text-right">{v.product_count}</td>
              <td className="text-right sans text-neutral-500">{money(v.avg_cost)}</td>
              <td className="text-right">{money(v.avg_wholesale)}</td>
              <td className="text-right">{pct(v.avg_margin_pct)}</td>
              <td className="text-right">{v.default_discount_pct == null ? '—' : `${v.default_discount_pct}%`}</td>
              <td className="text-right">
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => window.location.assign(`/admin?tab=products&supplier=${encodeURIComponent(v.name)}`)}
                    className="text-neutral-500 hover:text-black p-1"
                    data-testid={`vendor-filter-catalog-desktop-${v.name}`}
                    title="Filter products by this vendor"
                  >
                    <Package size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyDefault(v, false)}
                    disabled={busy === v.name || v.default_discount_pct == null || v.product_count === 0}
                    className="text-neutral-500 hover:text-black p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`vendor-apply-default-desktop-${v.name}`}
                    title="Apply default % to items missing one"
                  >
                    {busy === v.name ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(v)}
                    className="text-neutral-500 hover:text-black p-1"
                    data-testid={`vendor-edit-desktop-${v.name}`}
                    title="Edit vendor metadata"
                  >
                    <Pencil size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <VendorEditDialog
        vendor={editing}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onSaved={load}
      />
    </div>
  );
}

function VendorEditDialog({ vendor, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ default_discount_pct: '', notes: '', contact: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!vendor) return;
    setForm({
      default_discount_pct: vendor.default_discount_pct ?? '',
      notes: vendor.notes ?? '',
      contact: vendor.contact ?? '',
    });
  }, [vendor?.name]);

  if (!vendor) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/vendors/${encodeURIComponent(vendor.name)}`, {
        default_discount_pct: form.default_discount_pct === '' ? null : Number(form.default_discount_pct),
        notes: form.notes,
        contact: form.contact,
      });
      toast.success('Vendor updated');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="vendor-edit-dialog">
        <DialogHeader>
          <DialogTitle className="h-display text-2xl">{vendor.name}</DialogTitle>
          <p className="sans text-xs uppercase tracking-widest text-neutral-500">
            {vendor.product_count} product{vendor.product_count === 1 ? '' : 's'}
          </p>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="eyebrow mb-1 block">Default % off wholesale</label>
            <input
              type="number" step="0.1" min="0" max="99"
              value={form.default_discount_pct}
              onChange={(e) => setForm({ ...form, default_discount_pct: e.target.value })}
              className="ije-input h-10 w-full"
              placeholder="e.g. 46"
              data-testid="vendor-edit-default-pct"
            />
            <p className="sans text-[11px] text-neutral-500 mt-1">
              Reminder shown when adding a new item under this vendor. Use the ⚡ button on the vendor row to roll this out to their whole line.
            </p>
          </div>
          <div>
            <label className="eyebrow mb-1 block">Contact</label>
            <input
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              className="ije-input h-10 w-full"
              placeholder="Rep name, phone, or email"
              data-testid="vendor-edit-contact"
            />
          </div>
          <div>
            <label className="eyebrow mb-1 block">Notes</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="ije-input w-full resize-y"
              placeholder="Payment terms, lead times, MOQs, anything worth remembering…"
              data-testid="vendor-edit-notes"
            />
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-outline h-10" data-testid="vendor-edit-cancel">
            <X size={14} /> Cancel
          </button>
          <button type="button" onClick={save} disabled={saving} className="btn-primary h-10 min-w-[140px]" data-testid="vendor-edit-save">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save vendor</>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
